import { useCallback } from 'react';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import {
  useU2000DeleteHandlers,
  type U2000DeleteHandlersParams,
  type U2000DeleteHandlersReturn,
} from '../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import {
  useU2000GetHandlers,
  type U2000GetHandlersParams,
  type U2000GetHandlersReturn,
} from '../u2000Core/hooks/handler/U2000UseGetHandlers';
import {
  useU2000SaveHandlers,
  type U2000SaveHandlersParams,
  type U2000SaveHandlersReturn,
} from '../u2000Core/hooks/handler/U2000UseSaveHandlers';
import {
  useU2000SearchHandlers,
  type U2000SearchHandlersParams,
  type U2000SearchHandlersReturn,
} from '../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { U2000FormStateEnumEditMode } from '../u2000Core/reducer/formReducer/U2000FormState';
import {
  buildDeleteBody,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doInitCreateMinute,
  doSearch,
  saveMinutesForm,
  type U3139InitCreateMinute,
  type U3139InitCreateMinuteRequest,
  type U3139MinutesFormRow,
  type U3139MinutesSearchRow,
} from './U3139Service';

export interface U3139InitCreateHandlersParams {
  onError?: (error: unknown) => void;
}

export function useU3139InitCreateHandlers({ onError }: U3139InitCreateHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();

  const initCreateMinute = useCallback(
    async (body: U3139InitCreateMinuteRequest): Promise<U3139InitCreateMinute | null> => {
      try {
        const response = await doInitCreateMinute(axios, body);
        return (response.data?.result ?? null) as U3139InitCreateMinute | null;
      } catch (error) {
        onError?.(error);
        return null;
      }
    },
    [axios, onError],
  );

  return { initCreateMinute };
}

/** Recherche U3139 : axios encapsulé (ne pas appeler `doSearch` depuis le composant). */
export function useU3139SearchHandlers<T = U3139MinutesSearchRow>(
  params: Omit<U2000SearchHandlersParams<T>, 'doSearchFn'>,
): U2000SearchHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doSearchFn = useCallback(
    (searchPayload: Parameters<typeof doSearch>[1]) => doSearch(axios, searchPayload),
    [axios],
  );
  return useU2000SearchHandlers<T>({ ...params, doSearchFn });
}

/** Lecture d'une minute U3139. */
export function useU3139GetHandlers<T extends { id: string } = U3139MinutesFormRow>(
  params: Omit<U2000GetHandlersParams<T>, 'doGetFn'>,
): U2000GetHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doGetFn = useCallback((id: string) => doGetData(axios, id), [axios]);
  return useU2000GetHandlers<T>({ ...params, doGetFn });
}

/** Création / mise à jour U3139. */
export function useU3139SaveHandlers(
  params: Omit<U2000SaveHandlersParams<U3139MinutesFormRow>, 'doSaveFn'>,
): U2000SaveHandlersReturn<U3139MinutesFormRow> {
  const axios = useU2000AxiosAuthenticated();
  const doSaveFn = useCallback(
    (row: U3139MinutesFormRow, editMode: (typeof U2000FormStateEnumEditMode)[keyof typeof U2000FormStateEnumEditMode]) =>
      saveMinutesForm(row, editMode, axios),
    [axios],
  );
  return useU2000SaveHandlers<U3139MinutesFormRow>({ ...params, doSaveFn });
}

/** Suppression depuis la liste. */
export function useU3139ListDeleteHandlers(
  params: Omit<U2000DeleteHandlersParams<U3139MinutesSearchRow>, 'doDeleteFn'>,
): U2000DeleteHandlersReturn<U3139MinutesSearchRow> {
  const axios = useU2000AxiosAuthenticated();
  const doDeleteFn = useCallback(
    (id: string, row: U3139MinutesSearchRow) => doDeleteData(axios, id, buildDeleteBody(row)),
    [axios],
  );
  return useU2000DeleteHandlers<U3139MinutesSearchRow>({ ...params, doDeleteFn });
}

/** Suppression depuis la fiche. */
export function useU3139FormDeleteHandlers(
  params: Omit<U2000DeleteHandlersParams<U3139MinutesFormRow>, 'doDeleteFn'>,
): U2000DeleteHandlersReturn<U3139MinutesFormRow> {
  const axios = useU2000AxiosAuthenticated();
  const doDeleteFn = useCallback(
    (id: string, row: U3139MinutesFormRow) => doDeleteData(axios, id, buildDeleteBody(row)),
    [axios],
  );
  return useU2000DeleteHandlers<U3139MinutesFormRow>({ ...params, doDeleteFn });
}

/** Audits d'une minute U3139. */
export function useU3139AuditsFetcher() {
  const axios = useU2000AxiosAuthenticated();
  return useCallback((id: string) => doGetAuditsData(axios, id), [axios]);
}
